<template>
  <div class="vehicle-analysis-page">
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title"><i class="el-icon-truck"></i> 车辆分析</h2>
        <span class="page-desc">分析车辆出勤状态、收入排行及运营明细</span>
      </div>
    </div>
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="loadData">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 概览卡片 -->
    <div class="section-title"><i class="el-icon-truck"></i> 车辆概览</div>
    <div class="overview-cards">
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#409EFF,#66B1FF)"><i class="el-icon-truck"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.totalVehicles }}</div><div class="ov-card-label">车辆总数</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#67C23A,#85CE61)"><i class="el-icon-location"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.onlineVehicles }}</div><div class="ov-card-label">在线车辆</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#E6A23C,#EBB563)"><i class="el-icon-s-promotion"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.runningVehicles }}</div><div class="ov-card-label">运营中</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#909399,#A6A9AD)"><i class="el-icon-time"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.idleVehicles }}</div><div class="ov-card-label">空闲中</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#F56C6C,#F78989)"><i class="el-icon-warning"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.offlineVehicles }}</div><div class="ov-card-label">离线车辆</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#9B59B6,#B07CC6)"><i class="el-icon-s-order"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ stats.periodOrders }}</div><div class="ov-card-label">期间总工单</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#1abc9c,#2ecc71)"><i class="el-icon-money"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">¥{{ stats.periodRevenue }}</div><div class="ov-card-label">期间总收入</div></div>
      </div>
    </div>

    <!-- 地图 + 列表 -->
    <div class="map-section-grid">
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-map-location"></i> 车辆实时位置</span>
          <el-button size="mini" type="primary" plain @click="refreshVehicles" :loading="loading"><i class="el-icon-refresh"></i> 刷新</el-button>
        </div>
        <div class="map-container" id="vehicleMap"></div>
        <div class="map-legend">
          <div class="legend-item"><span class="legend-dot running"></span><span>运营中</span></div>
          <div class="legend-item"><span class="legend-dot idle"></span><span>空闲中</span></div>
          <div class="legend-item"><span class="legend-dot offline"></span><span>离线</span></div>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-s-grid"></i> 在线车辆列表</span>
          <el-input v-model="searchText" placeholder="搜索车辆号牌/司机" prefix-icon="el-icon-search" size="mini" clearable style="width: 180px"></el-input>
        </div>
        <div class="vehicle-list">
          <div
            class="vehicle-item"
            v-for="vehicle in filteredVehicles"
            :key="vehicle.id"
            :class="{ active: selectedVehicle && selectedVehicle.id === vehicle.id }"
            @click="selectVehicle(vehicle)"
          >
            <div class="vehicle-status" :class="vehicle.status"></div>
            <div class="vehicle-plate">{{ vehicle.plateNumber }}</div>
            <div class="vehicle-driver"><i class="el-icon-user"></i> {{ vehicle.driverName || '未绑定' }}</div>
            <div class="vehicle-line"><i class="el-icon-position"></i> {{ vehicle.lineName || '-' }}</div>
            <div class="vehicle-time">{{ vehicle.updateTime || '-' }}</div>
          </div>
          <el-empty v-if="filteredVehicles.length === 0" description="暂无在线车辆" :image-size="60"></el-empty>
        </div>
      </div>
    </div>

    <!-- 底部图表 2列 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-data-analysis"></i> 车辆出勤趋势</span>
          <el-radio-group v-model="attendanceTimeType" size="mini" @change="loadAttendanceTrend">
            <el-radio-button :label="0">今日</el-radio-button>
            <el-radio-button :label="7">近7天</el-radio-button>
            <el-radio-button :label="30">近30天</el-radio-button>
            <el-radio-button :label="180">近半年</el-radio-button>
            <el-radio-button :label="365">近一年</el-radio-button>
          </el-radio-group>
        </div>
        <div class="chart-body" ref="attendanceChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-pie-chart"></i> 车辆类型分布</span></div>
        <div class="chart-body" ref="vehicleTypeChart"></div>
      </div>
    </div>

    <!-- 车辆收入排行 2列 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-coin"></i> 车辆收入排行 TOP10</span>
          <el-radio-group v-model="vehicleRevenueTimeType" size="mini" @change="loadVehicleRevenueChart">
            <el-radio-button :label="0">今日</el-radio-button>
            <el-radio-button :label="2">近7天</el-radio-button>
            <el-radio-button :label="3">近30天</el-radio-button>
            <el-radio-button :label="4">近半年</el-radio-button>
            <el-radio-button :label="5">近一年</el-radio-button>
          </el-radio-group>
        </div>
        <div class="chart-body" ref="vehicleRevenueChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-s-data"></i> 车辆订单量排行 TOP10</span>
          <el-radio-group v-model="vehicleOrderTimeType" size="mini" @change="loadVehicleOrderChart">
            <el-radio-button :label="0">今日</el-radio-button>
            <el-radio-button :label="2">近7天</el-radio-button>
            <el-radio-button :label="3">近30天</el-radio-button>
            <el-radio-button :label="4">近半年</el-radio-button>
            <el-radio-button :label="5">近一年</el-radio-button>
          </el-radio-group>
        </div>
        <div class="chart-body" ref="vehicleOrderChart"></div>
      </div>
    </div>

    <!-- 车辆经营明细表 -->
    <div class="table-card" style="margin-bottom:20px">
      <div class="chart-card-header">
        <span><i class="el-icon-truck"></i> 车辆经营明细</span>
        <el-radio-group v-model="vehicleTableTimeType" size="mini" @change="loadVehicleTable">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <el-table :data="vehicleTableData" border stripe size="small" max-height="400" style="width:100%" v-loading="tableLoading">
        <el-table-column type="index" label="序" width="45" />
        <el-table-column prop="vehiclePlate" label="车辆号牌" min-width="100" />
        <el-table-column prop="driverName" label="当前司机" min-width="90" />
        <el-table-column prop="orderCount" label="工单数" min-width="70" sortable />
        <el-table-column prop="passengerCount" label="乘客数" min-width="70" sortable />
        <el-table-column label="总收入" min-width="110" sortable prop="totalRevenue">
          <template slot-scope="{row}"><span style="color:#67C23A;font-weight:600">¥{{ row.totalRevenue || '0.00' }}</span></template>
        </el-table-column>
        <el-table-column label="客单价" min-width="90" prop="avgOrderFee">
          <template slot-scope="{row}">¥{{ row.avgOrderFee || '0.00' }}</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "VehicleAnalysis",
  data() {
    return {
      loading: false,
      tableLoading: false,
      timeType: 0,
      searchText: "",
      selectedVehicle: null,
      stats: {
        totalVehicles: 0,
        onlineVehicles: 0,
        runningVehicles: 0,
        idleVehicles: 0,
        offlineVehicles: 0,
        periodOrders: 0,
        periodRevenue: 0
      },
      vehicleList: [],
      vehicleTableData: [],
      attendanceTimeType: 0,
      vehicleRevenueTimeType: 3,
      vehicleOrderTimeType: 3,
      vehicleTableTimeType: 3,
      map: null,
      markers: [],
      charts: {},
      resizeHandler: null
    };
  },
  computed: {
    filteredVehicles() {
      if (!this.searchText) return this.vehicleList;
      const keyword = (this.searchText || '').toLowerCase();
      return this.vehicleList.filter(v =>
        (v.plateNumber && v.plateNumber.toLowerCase().includes(keyword)) ||
        (v.driverName && v.driverName.toLowerCase().includes(keyword))
      );
    }
  },
  mounted() {
    this.initMap();
    this.loadData();
    this.resizeHandler = () => {
      this.map && this.map.resize && this.map.resize();
      Object.values(this.charts).forEach(chart => chart && chart.resize());
    };
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    this.map && this.map.destroy && this.map.destroy();
    Object.values(this.charts).forEach(chart => chart && chart.dispose());
  },
  methods: {
    formatMoney(num) {
      if (!num) return "0.00";
      return parseFloat(num).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async loadData() {
      this.loading = true;
      await Promise.all([
        this.loadStats(),
        this.loadVehicleList(),
        this.loadAttendanceTrend(),
        this.loadVehicleType(),
        this.loadVehicleRevenueChart(),
        this.loadVehicleOrderChart(),
        this.loadVehicleTable()
      ]);
      this.loading = false;
      this.$nextTick(() => setTimeout(() => Object.values(this.charts).forEach(c => c && c.resize()), 200));
    },
    async loadStats() {
      try {
        const [realtime, period] = await Promise.all([
          this.$axios.get("/api/v1/manage/statistics"),
          this.$axios.get("/api/v1/manage/analysis/vehicle", { timeType: this.timeType, limit: 200 })
        ]);
        const d = realtime?.data || {};
        this.stats.totalVehicles = d.vehicleNum || 0;
        this.stats.onlineVehicles = d.vehicleOnlineNum || 0;
        this.stats.runningVehicles = d.vehicleRunningNum || 0;
        this.stats.idleVehicles = Math.max(0, (d.vehicleOnlineNum || 0) - (d.vehicleRunningNum || 0));
        this.stats.offlineVehicles = Math.max(0, (d.vehicleNum || 0) - (d.vehicleOnlineNum || 0));

        const vList = period?.data || [];
        this.stats.periodOrders = vList.reduce((s, r) => s + (r.orderCount || 0), 0);
        this.stats.periodRevenue = vList.reduce((s, r) => s + parseFloat(r.totalRevenue || 0), 0).toFixed(2);
        this.$nextTick(() => this.renderVehicleStatus());
      } catch (e) {
        console.error("加载统计数据失败", e);
      }
    },
    async loadVehicleList() {
      try {
        const res = await this.$axios.get("/api/v1/manage/vehicle", { pageSize: 100, pageNum: 1 });
        const list = res?.data?.records || res?.data?.list || res?.data || [];
        this.vehicleList = list.map(item => ({
          id: item.id,
          plateNumber: item.vehicleNumber || item.plateNumber || "",
          driverName: item.driverName || "",
          driverId: item.driverId,
          lineName: item.lineName || "",
          status: item.runningStatus === 1 ? "running" : item.onlineStatus === 1 ? "idle" : "offline",
          lng: item.longitude || null,
          lat: item.latitude || null,
          updateTime: item.updateTime ? this.formatTime(new Date(item.updateTime * 1000)) : "-"
        }));
        this.updateMapMarkers();
      } catch (e) {
        this.vehicleList = [];
      }
    },
    formatTime(date) {
      const d = new Date(date);
      return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
    },
    initMap() {
      if (typeof AMap === "undefined") {
        console.error("高德地图未加载");
        return;
      }
      // 默认成都坐标
      let defaultCenter = [104.0668, 30.5728];
      
      this.map = new AMap.Map("vehicleMap", {
        zoom: 11,
        center: defaultCenter,
        mapStyle: "amap://styles/light"
      });
      
      // 基于机构名称获取城市坐标并设置地图中心
      this.setMapCenterByOrganization();
    },
    /**
     * 基于机构名称获取城市坐标
     */
    setMapCenterByOrganization() {
      const orgName = this.mixinUserInfo?.organizationName || this.mixinUserInfo?.companyName || '';
      if (!orgName || typeof AMap === "undefined") return;
      
      // 从机构名称中提取城市关键词
      const cityKeywords = this.extractCityFromName(orgName);
      if (!cityKeywords) return;
      
      AMap.plugin('AMap.Geocoder', () => {
        const geocoder = new AMap.Geocoder();
        geocoder.getLocation(cityKeywords, (status, result) => {
          if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
            const location = result.geocodes[0].location;
            if (this.map && location) {
              this.map.setCenter([location.lng, location.lat]);
            }
          }
        });
      });
    },
    /**
     * 从机构名称中提取城市关键词
     */
    extractCityFromName(name) {
      if (!name) return null;
      // 常见城市名称匹配
      const cityPatterns = [
        /成都/, /重庆/, /北京/, /上海/, /广州/, /深圳/, /杭州/, /南京/, /武汉/, /西安/,
        /天津/, /苏州/, /郑州/, /长沙/, /青岛/, /宁波/, /东莞/, /沈阳/, /昆明/, /合肥/,
        /绵阳/, /德阳/, /乐山/, /眉山/, /南充/, /泸州/, /宜宾/, /自贡/, /攀枝花/, /遂宁/,
        /内江/, /达州/, /广安/, /资阳/, /雅安/, /巴中/, /广元/, /凉山/, /甘孜/, /阿坝/
      ];
      
      for (const pattern of cityPatterns) {
        const match = name.match(pattern);
        if (match) {
          return match[0];
        }
      }
      
      // 如果没有匹配到城市，尝试使用机构名称前几个字符作为地址搜索
      return name.substring(0, 10);
    },
    updateMapMarkers() {
      if (!this.map) return;
      this.markers.forEach(marker => this.map.remove(marker));
      this.markers = [];

      this.vehicleList.forEach(vehicle => {
        if (!vehicle.lng || !vehicle.lat) return;
        
        const color = vehicle.status === "running" ? "#67C23A" : 
                      vehicle.status === "idle" ? "#E6A23C" : "#909399";
        
        const marker = new AMap.Marker({
          position: [vehicle.lng, vehicle.lat],
          content: `<div style="
            width: 24px; height: 24px; 
            background: ${color}; 
            border-radius: 50%; 
            border: 2px solid #fff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex; align-items: center; justify-content: center;
          ">
            <i class="el-icon-truck" style="color: #fff; font-size: 12px;"></i>
          </div>`,
          offset: new AMap.Pixel(-12, -12)
        });

        marker.on("click", () => this.selectVehicle(vehicle));
        
        const infoWindow = new AMap.InfoWindow({
          content: `
            <div style="padding: 8px; min-width: 150px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${vehicle.plateNumber}</div>
              <div style="font-size: 12px; color: #666;">司机：${vehicle.driverName || '未绑定'}</div>
              ${vehicle.lineName ? `<div style="font-size: 12px; color: #666;">线路：${vehicle.lineName}</div>` : ''}
              <div style="font-size: 12px; color: #999; margin-top: 4px;">更新：${vehicle.updateTime}</div>
            </div>
          `,
          offset: new AMap.Pixel(0, -30)
        });

        marker.on("mouseover", () => infoWindow.open(this.map, marker.getPosition()));
        marker.on("mouseout", () => infoWindow.close());

        this.map.add(marker);
        this.markers.push(marker);
      });
    },
    selectVehicle(vehicle) {
      this.selectedVehicle = vehicle;
      if (this.map && vehicle.lng && vehicle.lat) {
        this.map.setCenter([vehicle.lng, vehicle.lat]);
        this.map.setZoom(14);
      }
    },
    refreshVehicles() {
      this.loadVehicleList();
    },
    initChart(refName) {
      if (this.charts[refName]) this.charts[refName].dispose();
      const dom = this.$refs[refName];
      if (!dom) return null;
      const chart = echarts.init(dom);
      this.charts[refName] = chart;
      return chart;
    },
    async loadAttendanceTrend() {
      try {
        const ttMap = { 0: '0', 7: '2', 30: '3', 180: '4', 365: '5' };
        const res = await this.$axios.get("/api/v1/manage/statistics/report", {
          timeType: ttMap[this.attendanceTimeType] || '0'
        });
        const list = res?.data?.statisticsReportVos || [];
        const xData = list.map(item => {
          let t = item.time || "";
          if (t.includes(" ")) t = t.split(" ")[0];
          if (t.length === 10) t = t.substring(5);
          return t;
        });
        const onlineData = list.map(item => item.vehicleOnlineNum || 0);
        const runningData = list.map(item => item.vehicleRunningNum || 0);
        const isEmpty = list.length === 0;
        const option = {
          tooltip: { trigger: "axis", backgroundColor: "rgba(50,50,50,0.9)", textStyle: { color: "#fff" } },
          legend: { data: ["在线车辆", "运营车辆"], bottom: 0, textStyle: { color: "#666" } },
          grid: { left: "3%", right: "4%", bottom: "15%", top: "10%", containLabel: true },
          graphic: isEmpty ? [{ type: "text", left: "center", top: "middle", style: { text: "暂无数据", fill: "#c0c4cc", fontSize: 14 } }] : [],
          xAxis: { type: "category", data: xData, axisLine: { lineStyle: { color: "#ddd" } }, axisLabel: { color: "#666" } },
          yAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } }, axisLabel: { color: "#666" } },
          series: [
            { name: "在线车辆", type: "line", smooth: true, data: onlineData, itemStyle: { color: "#409EFF" }, areaStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:"rgba(64,158,255,0.3)"},{offset:1,color:"rgba(64,158,255,0.05)"}]) } },
            { name: "运营车辆", type: "line", smooth: true, data: runningData, itemStyle: { color: "#67C23A" }, areaStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:"rgba(103,194,58,0.2)"},{offset:1,color:"rgba(103,194,58,0.02)"}]) } }
          ]
        };
        const c = this.initChart('attendanceChart'); c && c.setOption(option);
      } catch (e) {
        console.error("加载出勤趋势失败", e);
      }
    },
    renderVehicleStatus() {
      const option = {
        tooltip: { trigger: "item", formatter: "{b}: {c}辆 ({d}%)" },
        legend: { orient: "vertical", right: "5%", top: "center", textStyle: { color: "#666" } },
        series: [{
          type: "pie", radius: ["40%", "65%"], center: ["38%", "50%"],
          label: { show: false },
          emphasis: { label: { show: true, fontSize: 13, fontWeight: "bold" } },
          data: [
            { value: this.stats.runningVehicles, name: "运营中", itemStyle: { color: "#67C23A" } },
            { value: this.stats.idleVehicles, name: "在线空闲", itemStyle: { color: "#409EFF" } },
            { value: this.stats.offlineVehicles, name: "离线", itemStyle: { color: "#909399" } }
          ]
        }]
      };
      const c = this.initChart('vehicleTypeChart'); c && c.setOption(option);
    },
    async loadVehicleType() {
      this.renderVehicleStatus();
    },
    async loadVehicleRevenueChart() {
      try {
        const res = await this.$axios.get("/api/v1/manage/analysis/vehicle", { timeType: this.vehicleRevenueTimeType, limit: 10 });
        const list = (res?.data || []).sort((a,b) => parseFloat(b.totalRevenue||0) - parseFloat(a.totalRevenue||0)).slice(0, 10);
        const names = list.map(v => v.vehiclePlate || "").slice().reverse();
        const values = list.map(v => parseFloat(v.totalRevenue || 0)).slice().reverse();
        const isEmpty = list.length === 0;
        const option = {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: p => `${p[0].name}<br/>收入: ¥<b>${p[0].value.toFixed(2)}</b>` },
          grid: { left: "3%", right: "12%", bottom: "3%", top: "3%", containLabel: true },
          graphic: isEmpty ? [{ type: "text", left: "center", top: "middle", style: { text: "暂无数据", fill: "#c0c4cc", fontSize: 14 } }] : [],
          xAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } }, axisLabel: { color: "#666", formatter: v => v >= 1000 ? (v/1000).toFixed(1)+"k" : v } },
          yAxis: { type: "category", data: names, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: "#606266", fontSize: 12 } },
          series: [{ type: "bar", data: values, barWidth: 14,
            itemStyle: { color: new echarts.graphic.LinearGradient(0,0,1,0,[{offset:0,color:"#67C23A"},{offset:1,color:"#85CE61"}]), borderRadius: [0,6,6,0] },
            label: { show: true, position: "right", color: "#666", fontSize: 11, formatter: p => "¥"+(p.value>=1000?(p.value/1000).toFixed(1)+"k":p.value.toFixed(0)) }
          }]
        };
        const c = this.initChart('vehicleRevenueChart'); c && c.setOption(option);
      } catch (e) { console.error("加载车辆收入排行失败", e); }
    },
    async loadVehicleOrderChart() {
      try {
        const res = await this.$axios.get("/api/v1/manage/analysis/vehicle", { timeType: this.vehicleOrderTimeType, limit: 10 });
        const list = (res?.data || []).sort((a,b) => (b.orderCount||0) - (a.orderCount||0)).slice(0, 10);
        const names = list.map(v => v.vehiclePlate || "").slice().reverse();
        const values = list.map(v => v.orderCount || 0).slice().reverse();
        const isEmpty = list.length === 0;
        const option = {
          tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: p => `${p[0].name}<br/>工单数: <b>${p[0].value}</b>` },
          grid: { left: "3%", right: "12%", bottom: "3%", top: "3%", containLabel: true },
          graphic: isEmpty ? [{ type: "text", left: "center", top: "middle", style: { text: "暂无数据", fill: "#c0c4cc", fontSize: 14 } }] : [],
          xAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } }, axisLabel: { color: "#666" } },
          yAxis: { type: "category", data: names, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: "#606266", fontSize: 12 } },
          series: [{ type: "bar", data: values, barWidth: 14,
            itemStyle: { color: new echarts.graphic.LinearGradient(0,0,1,0,[{offset:0,color:"#409EFF"},{offset:1,color:"#66B1FF"}]), borderRadius: [0,6,6,0] },
            label: { show: true, position: "right", color: "#666", fontSize: 11 }
          }]
        };
        const c = this.initChart('vehicleOrderChart'); c && c.setOption(option);
      } catch (e) { console.error("加载车辆订单排行失败", e); }
    },
    async loadVehicleTable() {
      this.tableLoading = true;
      try {
        const res = await this.$axios.get("/api/v1/manage/analysis/vehicle", { timeType: this.vehicleTableTimeType });
        this.vehicleTableData = (res?.data || []).sort((a,b) => parseFloat(b.totalRevenue||0) - parseFloat(a.totalRevenue||0));
      } catch (e) {
        this.vehicleTableData = [];
      }
      this.tableLoading = false;
    }
  }
};
</script>

<style lang="less" scoped>
.vehicle-analysis-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-bar {
    display: flex; align-items: center; justify-content: space-between;
    background: #fff; padding: 16px 20px; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 20px;
  }

  .section-title {
    font-size: 16px; font-weight: 600; color: #303133; margin-bottom: 16px;
    display: flex; align-items: center;
    i { margin-right: 8px; color: #409EFF; }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    .ov-card {
      background: #fff; border-radius: 10px; padding: 16px;
      display: flex; align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.2s ease;
      &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
      .ov-card-icon {
        width: 40px; height: 40px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        margin-right: 12px; flex-shrink: 0;
        i { color: #fff; font-size: 18px; }
      }
      .ov-card-info {
        min-width: 0;
        .ov-card-value { font-size: 20px; font-weight: 700; color: #303133; }
        .ov-card-label { font-size: 12px; color: #909399; margin-top: 2px; }
      }
    }
  }

  .chart-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; min-width: 0;

    .chart-card-header {
      padding: 14px 20px; border-bottom: 1px solid #f0f0f0;
      font-size: 14px; font-weight: 600; color: #303133;
      display: flex; align-items: center; justify-content: space-between;
      i { margin-right: 6px; color: #409EFF; }
    }
    .chart-body { width: 100%; height: 320px; padding: 10px; box-sizing: border-box; }
  }

  .map-section-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .charts-grid-half {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }

  .table-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    overflow: hidden;
    .chart-card-header {
      padding: 14px 20px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 14px; font-weight: 600; color: #303133;
      display: flex; align-items: center; justify-content: space-between;
      i { margin-right: 6px; color: #409EFF; }
    }
  }

  .map-container { height: 380px; }

  .map-legend {
    display: flex; justify-content: center; gap: 24px;
    padding: 12px; border-top: 1px solid #f0f0f0;
    .legend-item {
      display: flex; align-items: center; font-size: 12px; color: #666;
      .legend-dot {
        width: 10px; height: 10px; border-radius: 50%; margin-right: 6px;
        &.running { background: #67C23A; }
        &.idle { background: #E6A23C; }
        &.offline { background: #909399; }
      }
    }
  }

  .vehicle-list {
    height: 400px; overflow-y: auto; padding: 4px 8px;

    .vehicle-item {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 2px;
      gap: 12px;
      &:hover, &.active { background: #f5f7fa; }
      &.active { border-left: 3px solid #409EFF; padding-left: 9px; }

      .vehicle-status {
        width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        &.running { background: #67C23A; }
        &.idle { background: #E6A23C; }
        &.offline { background: #909399; }
      }
      .vehicle-plate {
        width: 90px; flex-shrink: 0;
        font-size: 13px; font-weight: 600; color: #303133;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .vehicle-driver {
        flex: 1; min-width: 0;
        font-size: 12px; color: #606266;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        i { margin-right: 3px; color: #909399; }
      }
      .vehicle-line {
        flex: 1; min-width: 0;
        font-size: 12px; color: #409EFF;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        i { margin-right: 2px; }
      }
      .vehicle-time {
        width: 65px; flex-shrink: 0;
        font-size: 11px; color: #c0c4cc; text-align: right;
      }
    }
  }
}
</style>
